import { ApiResponse } from '@models/api-response';
import { IPagedResponse, PagedResponse } from '@models/paged-response';
import { StandardHttpResponse } from '@models/standard-http-response';
import { copyArray, copyObject } from './object-helper';

/**
 * Creates an ApiResponse from a JSON object.
 * @param classCast Constructor function for response type.
 * @param res Object with response and messages fields.
 * @returns ApiResponse with response of type T.
 */
export function apiResponseFromJson<T>(classCast: new () => T, res: StandardHttpResponse): ApiResponse<T> {
  const apiResponse = new ApiResponse<T>();
  apiResponse.messages = res?.messages || [];
  apiResponse.success = true;
  apiResponse.response = copyObject(res.response, classCast);
  return apiResponse;
}

export function apiResponseFromJsonInterface<T>(res: StandardHttpResponse): ApiResponse<T | null> {
  const apiResponse = new ApiResponse<T | null>();
  apiResponse.messages = res?.messages || [];
  apiResponse.success = true;
  apiResponse.response =  res?.response ?? null;
  return apiResponse;
}

/**
 * Creates an ApiResponse from a JSON array.
 * @param classCast Constructor function for response type.
 * @param res Object with response and messages fields.
 * @returns ApiResponse with response array of type T.
 */
export function apiResponseFromArray<T>(classCast: new () => T, res: StandardHttpResponse): ApiResponse<T[]> {
  const apiResponse = new ApiResponse<T[]>();
  apiResponse.messages = res?.messages || [];
  apiResponse.success = true;
  apiResponse.response = copyArray(res.response, classCast);
  return apiResponse;
}

/**
 * Creates a PagedResponse from a JSON array.
 * @param classCast Constructor function for response type.
 * @param res Object with response and messages fields.
 * @returns PagedResponse with response array of type T.
 */
export function pagedResponseFromArray<T>(classCast: new () => T, res: StandardHttpResponse): IPagedResponse<T[]> {
  const pagedResponse = new PagedResponse<T[]>();
  pagedResponse.messages = res?.messages || [];
  pagedResponse.success = true;
  pagedResponse.response = copyArray(res.response, classCast);
  pagedResponse.pageNumber = res?.pageNumber;
  pagedResponse.pageSize = res?.pageSize;
  pagedResponse.totalPages = res?.totalPages;
  pagedResponse.totalRecords = res?.totalRecords;
  return pagedResponse;
}

/**
 * Creates a PagedResponse from a JSON array, trusting that the
 * response data matches the interface type T.
 * @param res Object with response and messages fields.
 * @returns PagedResponse with response array of type T.
 */
export function pagedResponseFromArrayInterface<T>(res: StandardHttpResponse): PagedResponse<T[]> {
  const pagedResponse = new PagedResponse<T[]>();
  pagedResponse.messages = res?.messages || [];
  pagedResponse.success = true;
  pagedResponse.response = res.response;
  pagedResponse.pageNumber = res?.pageNumber;
  pagedResponse.pageSize = res?.pageSize;
  pagedResponse.totalPages = res?.totalPages;
  pagedResponse.totalRecords = res?.totalRecords;
  return pagedResponse;
}
